import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Cache, cacheable } from '../../cache/index';
import { oneHourMilli } from '../../constants-types/constants';
import { BehavioralFactorRanges } from '../../core-data-model/BehavioralFactorRanges.model';
import { CognitiveJobAssessment } from '../../core-data-model/CognitiveJobAssessment.model';
import { CognitiveTargetQuestion } from '../../core-data-model/CognitiveTargetQuestion.model';
import { ActiveJobEntry } from '../../core-data-model/Job/ActiveJobCardData.model';
import { JobPipelineHealth } from '../../core-data-model/Job/JobPipelineHealth.model';
import { JobReportInfo } from '../../core-data-model/Job/JobReportInfo.model';
import { Job, JobUpdate } from '../../core-data-model/Job.model';
import {
    JobServer,
    CognitiveQuestionServer,
    ScoreTargetFactorsServer,
    ScoreTargetFactorsPost,
    ScoreJobCognitiveTargetServer,
    ScoreJobCognitiveTargetPost,
    JobServerV3,
    AssociatedPersonsServer,
    CreateJobAssessmentPost,
    CreateJobAssessmentServer,
    JobAssessmentServer,
    CognitiveJobAssessmentServer,
    ActiveJobsCardDataServerEntry,
    JobReportServer
} from '../../core-data-model/JobApi.serverinterface';
import { JobAssessment } from '../../core-data-model/JobAssessment.model';
import { JobChecklistQuestion } from '../../core-data-model/JobChecklistQuestion.model';
import { Person } from '../../core-data-model/Person.model';
import { JobReportEmailConfiguration } from '../../core-data-model/ReportEmailConfiguration.model';
import { PeopleAndJobsSearchResult } from '../../core-data-model/SearchApi.serverinterface';
import { SearchResult } from '../../core-data-model/SearchResult.model';
import { SearchState } from '../../core-data-model/SearchState.model';
import { AsyncFolderDecorator } from '../../core-services/Folders.dataservice';
import { IJobApi } from '../../core-services/JobApi/JobApi.connectinterface';
import { JobTransformers } from '../../core-services/JobApi.datatransform';
import { JobMatchQueryGeneratorService } from '../../core-services/JobMatchQueryGenerator.service';
import { JobsDataService } from '../../core-services/Jobs.dataservice';
import { SearchTransformers } from '../../core-services/SearchApi.datatransform';
import { HttpHeaderContentType } from '../../shared/Enums';
import { ApiService } from '../../system-services/Api.service';
import { InitDataService } from '../../system-services/InitData/InitData.service';
import { PdfReference } from '../../system-services/PdfReference';
import { UserProfileSettingsService } from '../../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../../translations';

@Injectable({
    providedIn: 'root'
})
export class JobApi implements IJobApi {
    private jobsCache = new Cache<Job[], string>(
        'jobsCache',
        (jobId: string) => jobId
    );
    private jobChecklistItemCache = new Cache<JobChecklistQuestion[], string>(
        'jobChecklistItemCache',
        (locale: string) => locale
    );
    private cognitiveQuestionsCache = new Cache<
        CognitiveTargetQuestion[],
        string
    >('cognitiveQuestionsCache', (locale: string) => locale);
    activeJobEntryCache = new Cache<ActiveJobEntry[], string>(
        'activeJobEntryCache',
        (companyId: string) => companyId
    );

    constructor(
        private api: ApiService,
        private initData: InitDataService,
        private userProfile: UserProfileSettingsService,
        private jobMatchQueryGenerator: JobMatchQueryGeneratorService,
        private jobTransformers: JobTransformers,
        private jobsDataService: JobsDataService,
        private searchTransformers: SearchTransformers,
        private TR: TranslationService
    ) {}

    async createJob(
        job: JobUpdate,
        companyId: string = this.userProfile.currentCompany
    ): Promise<Job> {
        return this.api
            .post(
                `/api/v3/companies/${companyId}/jobs/`,
                this.jobTransformers.transformClientToServer(
                    job,
                    companyId,
                    this.initData.user.userId
                )
            )
            .then(([response]) => {
                const ourJob = this.jobTransformers.transform(response);
                // the following is asynchronous, but doesn't need to return to keep going
                this.jobsDataService.addToMostRecentJobs(ourJob);
                this.activeJobEntryCache.clearAll();
                return ourJob;
            });
    }

    private getJobData(jobId: string, companyId: string) {
        return this.api.get<JobServer>(
            `/api/v3/companies/${companyId}/jobs/${jobId}`
        );
    }

    async getJob(jobId: string, companyId = this.userProfile.currentCompany) {
        const [response] = await this.getJobData(jobId, companyId);
        const job = this.jobTransformers.transform(response);
        this.jobsDataService.addToMostRecentJobs(job);
        return job;
    }

    getJobs(companyId = this.userProfile.currentCompany): Promise<Job[]> {
        return this.getJobsData(companyId).then(([response]) => {
            const jobs = response.map((jobServer) =>
                this.jobTransformers.transform(jobServer)
            );

            return jobs;
        });
    }

    getJobPipelineHealth(
        jobId: string,
        companyId = this.userProfile.currentCompany
    ): Promise<JobPipelineHealth> {
        return this.api
            .get<AssociatedPersonsServer>(
                `/api/v3/companies/${companyId}/jobs/${jobId}/associatedPersons`
            )
            .then(([associatedPersons]) =>
                this.jobTransformers.transformPipelineHealth(
                    jobId,
                    associatedPersons
                )
            );
    }

    @AsyncFolderDecorator.waitForFolders()
    getJobMatches(
        jobId: string,
        pristineSearchState: SearchState,
        companyId = this.userProfile.currentCompany
    ): Promise<SearchResult[] | null> {
        return this.api.multiplexSearchRequest(
            pristineSearchState,
            (searchState) => {
                const oDataQuery =
                    this.jobMatchQueryGenerator.createODataString(searchState);
                return this.api
                    .get<AssociatedPersonsServer>(
                        `/api/v3/companies/${companyId}/jobs/${jobId}/associatedPersons?${oDataQuery}`
                    )
                    .then(([result]) => {
                        pristineSearchState.pageState.maxResults =
                            result.totalSearchResults;
                        return result.jobAssociatedPeople.map((match) =>
                            this.jobTransformers.transformPersonMatches(match)
                        );
                    });
            }
        );
    }

    @AsyncFolderDecorator.waitForFolders()
    getJobApplicant(
        jobId: string,
        personId: string,
        companyId = this.userProfile.currentCompany
    ): Promise<Person | null> {
        return this.api
            .get<AssociatedPersonsServer>(
                `/api/v3/companies/${companyId}/jobs/${jobId}/associatedPersons?$filter=jobAssociatedPeople/person/assessmentUser/assessmentUserId eq '${personId}'`
            )
            .then(
                ([result]) =>
                    result.jobAssociatedPeople.map((match) =>
                        this.jobTransformers.transformPersonMatches(match)
                    )[0]
            );
    }

    getJobRoleModels(
        jobId: string,
        companyId = this.userProfile.currentCompany
    ): Promise<Person[]> {
        return this.api
            .get<PeopleAndJobsSearchResult>(
                `/api/v3/companies/${companyId}/jobs/${jobId}/associatedRoleModels`
            )
            .then(([results]) => {
                if (!results || !results.peopleAndJobsSearchResult) {
                    return [];
                }
                return results.peopleAndJobsSearchResult.map(
                    (entry) =>
                        this.searchTransformers.transformPeopleAndJobs(
                            entry
                        ) as Person
                );
            });
    }

    addJobRoleModel(
        jobId: string,
        personIds: string[],
        companyId = this.userProfile.currentCompany
    ) {
        return this.api.post(
            `/api/v3/companies/${companyId}/jobs/${jobId}/associatedRoleModels`,
            personIds
        );
    }

    @cacheable(
        function (this: JobApi) {
            return this.jobChecklistItemCache;
        },
        (locale: string) => locale,
        null,
        null,
        // We'll assume these will never change within a given session
        -1
    )
    private getChecklistQuestionsServer(locale: string) {
        return this.api.get<string[]>(`/api/v3/jobs/checklistItems/${locale}`);
    }

    getChecklistQuestions(
        // TODO: get this from somewhere
        locale: string = this.TR.currentLocale
    ): Promise<JobChecklistQuestion[]> {
        return this.getChecklistQuestionsServer(locale).then(([questions]) =>
            this.jobTransformers.transformChecklistQuestions(questions)
        );
    }

    @cacheable(
        function (this: JobApi) {
            return this.cognitiveQuestionsCache;
        },
        (locale: string) => locale,
        null,
        null,
        // We'll assume these will never change within a given session
        -1
    )
    private getCognitiveQuestionsServer(locale: string) {
        return this.api.get<CognitiveQuestionServer[]>(
            `/api/v3/jobs/cognitiveItems/${locale}`
        );
    }

    getCognitiveQuestions(
        locale: string = this.TR.currentLocale
    ): Promise<CognitiveTargetQuestion[]> {
        return this.getCognitiveQuestionsServer(locale).then(([questions]) =>
            questions.map((question) =>
                this.jobTransformers.transformCognitiveQuestion(question)
            )
        );
    }

    getBehavioralJobScore(
        questions: JobChecklistQuestion[]
    ): Promise<BehavioralFactorRanges> {
        return this.api
            .post<
                ScoreTargetFactorsPost,
                ScoreTargetFactorsServer
            >(`/api/v3/jobs/scoreTargetFactors`, this.jobTransformers.transformBehavioralJobScorePost(questions))
            .then(([server]) =>
                this.jobTransformers.transformBehavioralJobScore(server)
            );
    }

    getCognitiveJobScore(
        questions: CognitiveTargetQuestion[],
        onetCode: string
    ): Promise<number> {
        // Don't send the onetCode if it's null. Causes a server-side error
        const queryParams = onetCode ? `?onetSocCode=${onetCode}` : '';

        return this.api
            .post<
                ScoreJobCognitiveTargetPost,
                ScoreJobCognitiveTargetServer
            >('/api/v3/jobs/scoreJobCognitiveTarget' + queryParams, this.jobTransformers.transformCognitiveJobScorePost(questions))
            .then(([server]) => server.cognitiveTarget);
    }

    getCognitiveBaselineNormalizedJobScore(
        rawScore: number,
        onetCode: string
    ): Promise<number> {
        if (onetCode === null) {
            return this.api
                .get<number>(
                    `/api/v3/jobs/getNormalizedBaselineCogJobTarget?rawScore=${rawScore}`
                )
                .then(([server]) => server);
        } else {
            return this.api
                .get<number>(
                    `/api/v3/jobs/getNormalizedBaselineCogJobTarget?rawScore=${rawScore}&onetSocCode=${onetCode}`
                )
                .then(([server]) => server);
        }
    }

    updateJob(job: JobUpdate, companyId = this.userProfile.currentCompany) {
        const id = job instanceof Job ? job.id : job.originalObject.id;
        const body = this.jobTransformers.transformJobUpdateServerV3Patch(job);
        return this.api
            .patch<JobServerV3>(
                `/api/v3/companies/${companyId}/jobs/${id}`,
                body
            )
            .then(() => {
                this.activeJobEntryCache.clearAll();
                this.jobsCache.clear(id);
            });
    }

    archive(jobId: string, companyId = this.userProfile.currentCompany) {
        return this.api
            .delete(`/api/v3/companies/${companyId}/jobs/${jobId}`)
            .then(() => {
                this.activeJobEntryCache.clearAll();
                this.jobsDataService.removeFromMostRecentJobs(jobId);
            });
    }

    createJobAssessments(
        job: Job,
        sender: { email: string; firstName: string; lastName: string },
        recipients: { email: string; fullName: () => string }[],
        companyId = this.userProfile.currentCompany
    ): Promise<JobAssessment[]> {
        return this.api
            .post<CreateJobAssessmentPost[], CreateJobAssessmentServer[]>(
                `/api/v2/companies/${companyId}/jobassessments/synchronousbatch`,
                recipients.map((r) =>
                    this.jobTransformers.transformCreateJobAssessmentPost(
                        job,
                        sender,
                        r
                    )
                )
            )
            .then(([serverAssessments]) =>
                serverAssessments.map((s) =>
                    this.jobTransformers.transformCreateJobAssessmentServer(s)
                )
            );
    }

    createAndScoreJobAssessments(
        job: Job,
        sender: { email: string; firstName: string; lastName: string },
        recipient: { email: string; fullName: () => string },
        checklist: JobChecklistQuestion[],
        companyId = this.userProfile.currentCompany
    ): Promise<any> {
        return this.api.post<CreateJobAssessmentPost, any>(
            `/api/v2/companies/${companyId}/jobassessments/CreateAndScore`,
            this.jobTransformers.transformCreateJobAssessmentPost(
                job,
                sender,
                recipient,
                checklist
            )
        );
    }

    getJobAssessmentsForJob(
        jobId: string,
        companyId = this.userProfile.currentCompany
    ): Promise<JobAssessment[]> {
        return this.api
            .get<
                JobAssessmentServer[]
            >(`/api/v3/companies/${companyId}/jobs/${jobId}/associatedJobAssessments`)
            .then(([assessments]) =>
                this.jobTransformers.transformJobAssessmentsServer(
                    assessments,
                    jobId
                )
            );
    }

    getCognitiveJobAssessmentsForJob(
        job: Job,
        companyId = this.userProfile.currentCompany
    ): Promise<CognitiveJobAssessment[]> {
        return this.api
            .get<
                CognitiveJobAssessmentServer[]
            >(`/api/v3/companies/${companyId}/jobs/${job.id}/associatedCognitiveJobAssessments`)
            .then(([assessments]) =>
                this.jobTransformers.transformCognitiveJobAssessmentsServer(
                    assessments,
                    job
                )
            );
    }

    getJobReportV2Pdf(
        jId: string,
        includeCognitive = true,
        languageLocale: string = this.TR.currentLocale,
        companyId = this.userProfile.currentCompany
    ): Promise<SafeResourceUrl> {
        const contentType = HttpHeaderContentType.PDF; // probably should be specifying this on the request

        return this.api.processResourceUrl(
            this.api.getResourceUrl(
                `/api/v3/companies/${companyId}/jobs/downloadV2JobReports`,
                contentType,
                true,
                {
                    reportLanguage: languageLocale,
                    includeCognitiveTarget: includeCognitive,
                    jobsToReportOn: [jId]
                }
            ),
            contentType
        );
    }

    getJobReportV2Bulk(
        jobIdsToReportOn: string[],
        includeCognitive = true,
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<PdfReference> {
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/companies/${companyId}/jobs/PrintV2JobReports`,
            HttpHeaderContentType.PDF,
            true,
            {
                reportLanguage: languageLocale,
                includeCognitiveTarget: includeCognitive,
                jobsToReportOn: jobIdsToReportOn
            }
        );
    }

    sendBulkJobsReportV2Email(
        jobIdsToReportOn: string[],
        includeCognitive: boolean,
        languageLocale: string = this.TR.currentLocale,
        reportEmailConfig: JobReportEmailConfiguration,
        companyId: string = this.userProfile.currentCompany
    ) {
        const payload = this.jobTransformers.transformToServerReportConfig(
            jobIdsToReportOn,
            includeCognitive,
            languageLocale,
            reportEmailConfig
        );

        return this.api.post(
            `/api/v3/companies/${companyId}/jobs/EmailV2JobReports`,
            payload
        );
    }

    getPeopleWhoMatchJob(
        jobId: string,
        searchState: SearchState,
        companyId: string = this.userProfile.currentCompany
    ): Promise<Person[]> {
        return this.api
            .get(
                `/api/v3/companies/${companyId}/jobs/${jobId}/peopleByMatchScore?$top=${searchState.pageState.resultsPerPage}`
            )
            .then(([response]) =>
                this.jobTransformers.transformPeopleMatchesToPeople(response)
            );
    }

    getJobReportInfo(
        jobId: string,
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ): Promise<JobReportInfo> {
        return this.getJobReportInfoData(jobId, companyId, languageLocale).then(
            ([response]) => {
                const jobReportInfo = this.jobTransformers.transformToJobReport(
                    response,
                    jobId
                );
                return jobReportInfo;
            }
        );
    }

    @cacheable(
        function (this: JobApi) {
            return this.activeJobEntryCache;
        },
        (modifiedByMe: boolean, companyId: string) =>
            companyId + `${modifiedByMe ? 'true' : 'false'}`,
        null,
        null,
        oneHourMilli
    )
    private getRecentlyModifiedJobInfoServer(
        modifiedByMe: boolean,
        companyId: string
    ) {
        return this.api.get<ActiveJobsCardDataServerEntry[]>(
            `/api/v3/companies/${companyId}/jobs/recentlymodifiedjobinfo${
                modifiedByMe
                    ? `?modifiedByUserId=${this.initData.user.userGuid}`
                    : ''
            }`
        );
    }

    getActiveJobsCardData(
        modifiedByMe = false,
        companyId: string = this.userProfile.currentCompany
    ): Promise<ActiveJobEntry[]> {
        return this.getRecentlyModifiedJobInfoServer(
            modifiedByMe,
            companyId
        ).then(([data]) => this.jobTransformers.transformActiveJobsData(data));
    }

    private getJobReportInfoData(
        jobId: string,
        companyId: string,
        languageLocale: string = this.TR.currentLocale
    ) {
        return this.api.get<JobReportServer>(
            `/api/v3/companies/${companyId}/jobs/${jobId}/jobReportInfo?languageLocale=${languageLocale}`
        );
    }

    private getJobsData(companyId: string) {
        return this.api.get<JobServer[]>(`/api/v3/companies/${companyId}/jobs`);
    }
}
